"use client";

import { useEffect, useSyncExternalStore } from "react";
import { useStoredValue } from "@/lib/use-stored-value";

type Theme = "light" | "dark" | "system";

function subscribeToColourScheme(onChange: () => void) {
  const query = window.matchMedia("(prefers-color-scheme: dark)");
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}

function prefersDark() {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function serverPrefersDark() {
  return false;
}

export function ThemeToggle() {
  const [storedTheme, setTheme] = useStoredValue("theme", "system", null);
  const theme: Theme = storedTheme === "light" || storedTheme === "dark" ? storedTheme : "system";
  const systemDark = useSyncExternalStore(subscribeToColourScheme, prefersDark, serverPrefersDark);
  const isDark = theme === "dark" || (theme === "system" && systemDark);

  useEffect(() => {
    if (storedTheme === null) return;
    document.documentElement.classList.toggle("dark", isDark);
  }, [storedTheme, isDark]);

  if (storedTheme === null) return <div className="w-9 h-9" />;

  function cycle() {
    setTheme(theme === "system" ? "dark" : theme === "dark" ? "light" : null);
  }

  return (
    <button
      onClick={cycle}
      className="flex items-center justify-center w-9 h-9 rounded-sm transition-colors hover:bg-forest/[0.06] dark:hover:bg-white/[0.08] bg-transparent border-none cursor-pointer"
      aria-label={`Theme: ${theme}. Click to change.`}
      title={`Theme: ${theme}`}
    >
      {isDark ? (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-text-body">
          <circle cx="12" cy="12" r="5" />
          <line x1="12" y1="1" x2="12" y2="3" />
          <line x1="12" y1="21" x2="12" y2="23" />
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
          <line x1="1" y1="12" x2="3" y2="12" />
          <line x1="21" y1="12" x2="23" y2="12" />
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
        </svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-text-body">
          <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
        </svg>
      )}
    </button>
  );
}
